/*
 * pure dialog behaviour for the teacher-payment dialogs (see tp_dialog.jsx). Focus-trap arithmetic
 * and the discard guard live here so they are unit-tested, not eyeballed.
 */

// Tab order candidates inside a dialog; [tabindex="-1"] is programmatic focus only.
export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

/*
 * Where Tab/Shift+Tab must land to stay inside the dialog, as an index into `elements`: -1 means
 * "let the browser move focus normally".
 */
export const trap_index = (elements, active, shift) => {
  const list = Array.isArray(elements) ? elements : [];
  if (list.length === 0) return -1;
  const at = list.indexOf(active);
  if (at === -1) return shift ? list.length - 1 : 0;
  if (shift && at === 0) return list.length - 1;
  if (!shift && at === list.length - 1) return 0;
  return -1;
};

// No element holds focus: it fell to <body> (or nowhere) when the focused control unmounted.
export const is_focus_lost = (active, body) => !active || active === body;

// Focus is on <body>, or outside this dialog and every other one (a popover portaled out owns its own).
export const focus_left_dialog = (active, body, panel) =>
  is_focus_lost(active, body) ||
  (!panel.contains(active) && !(active.closest && active.closest('[role="dialog"]')));

export const DISCARD_GUARD_TEXT =
  'This dialog was not closed: a reason has been typed. Use Cancel to discard it.';

// A stray Escape or overlay click must not throw away a typed reason (and, in the close dialog, a
// large selection with it). The explicit affordances — Cancel, the ✕ — still close.
export const discard_guard_text = (...reasons) =>
  reasons.some((r) => String(r === null || r === undefined ? '' : r).trim().length > 0)
    ? DISCARD_GUARD_TEXT
    : '';
